import { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { Link } from 'dibs-elements/exports/Link.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Header } from 'dibs-elements/exports/Header.tsx';

// components
import { viewMore } from '../../../shared/sharedMessages.jsx';
import { PageLayout } from 'dibs-elements/exports/PageLayout.tsx';

// styles
import styles from './PdpRespSimilarItemsContainer.scss';

function PdpRespSimilarItemsContainerComponent(props) {
    const {
        header,
        subtitle,
        subHeader,
        children,
        dataTn,
        gridWrapperClass,
        gridHeaderClass,
        isUnavailablePdp,
        isHorizontal,
        item,
        scrollToRef,
        isSponsored,
        ...options
    } = props;

    const { seller, isSold } = item || {};
    const { isAccessible } = seller || {};
    const isNotListedOrSold = !isAccessible;

    const wrapperClass = classnames(styles.wrapper, {
        [styles.hasSubtitle]: !!subtitle,
        [styles.isTopContainer]: options.isTopContainer,
    });
    const contentClass = classnames(styles.content, gridWrapperClass, {
        [styles.isHorizontal]: isHorizontal,
    });
    const subtitleClass = classnames(isNotListedOrSold ? styles.subtitleSoldPdp : styles.subtitle, {
        [styles.hasHeader]: !!header,
    });
    const headerClass = classnames(styles.header, gridHeaderClass, {
        [styles.isUnavailablePdp]: isUnavailablePdp,
    });

    const pageLayoutProps = {
        setViewportBackground: options.isDark, // to set the default dark viewport background
        // to remove the offets so that the the default layout would be coming from main PDP container
        setOffset: options.isDark,
    };

    return (
        <PageLayout {...pageLayoutProps} dataTn={dataTn}>
            {/* give wrapper tabIndex to allow focus on "view more" scroll to */}
            <div tabIndex={-1} ref={scrollToRef} className={wrapperClass}>
                {/* Header */}
                {header && (
                    <HeadingLevel>
                        {Heading =>
                            isSold ? (
                                <Fragment>
                                    <div className={headerClass}>
                                        <Header
                                            htmlElementType={Heading}
                                            title={header}
                                            dataTn="dlos-header"
                                        />
                                    </div>
                                    {!options.hideBar && <div className={styles.soldHeader} />}
                                </Fragment>
                            ) : (
                                <Heading className={headerClass}>{header}</Heading>
                            )
                        }
                    </HeadingLevel>
                )}

                {/* FULL WIDTH OF PAGE */}
                <div
                    className={contentClass}
                    data-tn={`pdp-similar${isSponsored ? '-sponsored' : ''}-items`}
                >
                    {!!subHeader && subHeader}
                    {!!subtitle && (
                        <div className={subtitleClass}>
                            {subtitle}{' '}
                            <Link
                                className={classnames(styles.viewMore, {
                                    [styles.viewMoreSoldPdp]: isNotListedOrSold,
                                })}
                                onClick={options.onClickViewMore}
                                dataTn="view-more-similar-items"
                            >
                                {viewMore}
                            </Link>
                        </div>
                    )}
                    {/* Grid of Items */}
                    {children}
                </div>
            </div>
        </PageLayout>
    );
}

PdpRespSimilarItemsContainerComponent.propTypes = {
    header: PropTypes.node,
    subHeader: PropTypes.node,
    subtitle: PropTypes.node,
    children: PropTypes.node,
    gridWrapperClass: PropTypes.string,
    gridHeaderClass: PropTypes.string,
    dataTn: PropTypes.string.isRequired,
    item: PropTypes.object.isRequired,
    scrollToRef: PropTypes.object,

    // options
    isDark: PropTypes.bool,
    isHorizontal: PropTypes.bool,
    hideBar: PropTypes.bool,
    onClickViewMore: PropTypes.func,
    isTopContainer: PropTypes.bool,
    isUnavailablePdp: PropTypes.bool,
    isSponsored: PropTypes.bool,
};

export const PdpRespSimilarItemsContainer = createFragmentContainer(
    PdpRespSimilarItemsContainerComponent,
    {
        item: graphql`
            fragment PdpRespSimilarItemsContainer_item on Item {
                isSold
                seller {
                    isAccessible
                }
            }
        `,
    }
);
